import React, { memo } from 'react';
import client from '@atlassian/jira-apollo-gira/src/index.tsx';
import { ApolloClientProvider } from '@atlassian/jira-apollo-multiple-clients/src/main.tsx';
import BusinessAppWrapper from '@atlassian/jira-business-app-wrapper/src/ui/app-wrapper/index.tsx';
import { IssueTypesAndFieldsContext } from '@atlassian/jira-business-entity-project/src/controllers/issue-types-and-fields-context/index.tsx';
import { ExperienceErrorBoundary } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { IssueCreateProvider } from '@atlassian/jira-business-issue-create/src/controllers/issue-create-context/index.tsx';
import { IssueEventProvider } from '@atlassian/jira-business-issue-events-observable/src/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { MAIN_EXPERIENCE } from './common/constants.tsx';
import { ConnectedProjectsProvider } from './controllers/connected-projects-multi/index.tsx';
import { useCalendarFeatures } from './controllers/features-context/index.tsx';
import { useCreateIssue } from './services/create-issue/index.tsx';
import type { CalendarViewAnalyticsContext } from './types.tsx';
import CalendarView from './ui/calendar-view/index.tsx';

const analyticsSourceName = 'calendarView';
const analyticsContext: CalendarViewAnalyticsContext = {
	viewType: 'month',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CalendarViewRoot = memo<Record<any, any>>(() => {
	const { hasInlineCreate, hasFilterCustomFields } = useCalendarFeatures();
	const isUsingIssueTypesAndFields = hasInlineCreate || hasFilterCustomFields;
	const services = useCreateIssue();

	const content = (
		<ConnectedProjectsProvider>
			<CalendarView />
		</ConnectedProjectsProvider>
	);

	return (
		<IssueEventProvider>
			<BusinessAppWrapper withSidebar>
				<ContextualAnalyticsData
					objectType="calendar"
					attributes={analyticsContext}
					sourceType={SCREEN}
					sourceName={analyticsSourceName}
				>
					{isUsingIssueTypesAndFields ? (
						<IssueTypesAndFieldsContext>
							{hasInlineCreate ? (
								<IssueCreateProvider
									onCreateIssue={services.createIssue}
									onRefetchIssue={services.refetchIssue}
								>
									{content}
								</IssueCreateProvider>
							) : (
								content
							)}
						</IssueTypesAndFieldsContext>
					) : (
						content
					)}
					<FireScreenAnalytics />
				</ContextualAnalyticsData>
			</BusinessAppWrapper>
		</IssueEventProvider>
	);
});

const Calendar = () => (
	<ExperienceErrorBoundary experience={MAIN_EXPERIENCE} shouldStartExperience>
		<ApolloClientProvider client={client}>
			<CalendarViewRoot />
		</ApolloClientProvider>
	</ExperienceErrorBoundary>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default memo<Record<any, any>>(Calendar);
